// src/pages/student/StudentLayout.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Badge, Button, Dropdown } from "react-bootstrap";
import { signOut } from "firebase/auth";
import { auth, db, storage } from "../../services/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { useTheme } from "../../context/ThemeContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import ParticlesBackground from "../../components/ParticlesBackground";
import "./_theme.css";
import { ref, getDownloadURL, listAll, getMetadata } from "firebase/storage";
import { useAuth } from "../../context/AuthContext";
import { useBrand, hexToRgba } from "./_brand";

/* ───────── 3D icon assets (adjust paths to your files) ───────── */
import SDash3D       from "../../assets/3d/dashboard.png";
import SProfile3D    from "../../assets/3d/profile.png";
import SFees3D       from "../../assets/3d/fees.png";
import SClasses3D    from "../../assets/3d/online-class.png";
import SExams3D      from "../../assets/3d/exams.png";
import SAssign3D     from "../../assets/3d/assignments.png";
import SProgress3D   from "../../assets/3d/progress.png";
import SAttend3D     from "../../assets/3d/attendance.png";
import SCirculars3D  from "../../assets/3d/megaphone.png";
import SSupport3D    from "../../assets/3d/support.png";
import SSettings3D   from "../../assets/3d/settings.png";

const STUDENT_3D = {
  dashboard: SDash3D,
  profile: SProfile3D,
  fees: SFees3D,
  classes: SClasses3D,
  exams: SExams3D,
  assignments: SAssign3D,
  progress: SProgress3D,
  attendance: SAttend3D,
  circulars: SCirculars3D,
  support: SSupport3D,
  settings: SSettings3D,
};

/* ---------------- color constants ---------------- */
const ACCENTS = [
  "#0d6efd", "#3b82f6", "#1d4ed8", "#60a5fa",
  "#0ea5e9", "#38bdf8", "#0284c7", "#4f46e5", "#4338ca",
  "#dc3545", "#ef4444", "#991b1b", "#f87171", "#f43f5e", "#e11d48", "#b91c1c", "#fb7185", "#fca5a5", "#be123c",
  "#198754", "#22c55e", "#065f46", "#86efac", "#10b981", "#059669", "#16a34a", "#15803d", "#84cc16", "#a3e635",
  "#ffc107", "#facc15", "#d97706", "#fde047", "#f59e0b", "#eab308", "#ca8a04", "#ffd166",
  "#6f42c1", "#8b5cf6", "#7e22ce", "#c4b5fd", "#a855f7", "#9333ea", "#7c3aed", "#d8b4fe",
  "#20c997", "#06b6d4", "#164e63", "#67e8f9", "#22d3ee", "#14b8a6", "#0d9488", "#2dd4bf",
  "#fd7e14", "#f97316", "#9a3412", "#fb923c", "#ea580c", "#c2410c",
  "#ec4899", "#db2777", "#be185d", "#f472b6", "#d946ef", "#fda4af",
  "#6c757d", "#94a3b8", "#334155", "#e2e8f0", "#9ca3af", "#6b7280", "#374151", "#1f2937",
  "#111827", "#f3f4f6", "#f8fafc", "#fafafa",
  "#000000", "#ffffff",
];

/* ---------------- logo resolvers ---------------- */
const resolveLogoUrl = async (dataOrPath) => {
  const d = typeof dataOrPath === "string" ? { logoPath: dataOrPath } : (dataOrPath || {});
  const direct = d.logoUrl || d.logoURL;
  if (direct) return direct;
  const path = d.logoPath || d.logo;
  if (!path) return "";
  try { return await getDownloadURL(ref(storage, path)); } catch { return ""; }
};
async function resolveLogoFromBrandingFolder(schoolId) {
  try {
    const folderRef = ref(storage, `schools/${schoolId}/branding`);
    const { items } = await listAll(folderRef);
    if (!items.length) return "";
    const withMeta = await Promise.all(
      items.map(async (it) => {
        try { const meta = await getMetadata(it); return { it, t: new Date(meta.updated || 0).getTime() || 0 }; }
        catch { const m = it.name.match(/(\d{13})/); return { it, t: m ? Number(m[1]) : 0 }; }
      })
    );
    withMeta.sort((a, b) => b.t - a.t);
    return await getDownloadURL(withMeta[0].it);
  } catch { return ""; }
}

/* ---------------- helpers ---------------- */
const normalizeHex6 = (hex) => {
  let v = String(hex || "").trim().replace("#", "");
  if (v.length === 3) v = v.split("").map((x) => x + x).join("");
  if (/^[0-9a-f]{6}$/i.test(v)) return `#${v.toLowerCase()}`;
  return null;
};
const onColorFor = (hex) => {
  const h = normalizeHex6(hex);
  if (!h) return "#fff";
  const n = parseInt(h.slice(1), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#111" : "#fff";
};
const paint = (el, { primary, accent }) => {
  if (!el) return;
  el.style.setProperty("--brand-primary", primary);
  el.style.setProperty("--brand-accent", accent);
  el.style.setProperty("--on-primary", onColorFor(primary));
  el.style.setProperty("--on-accent", onColorFor(accent));
  el.style.setProperty("--brand-accent-soft", hexToRgba(accent, 0.08));

  const prefersDark =
    document.documentElement.classList.contains("dark") ||
    (getComputedStyle(document.documentElement).getPropertyValue("--theme") || "").trim() === "dark";
  const base = prefersDark ? "#0b0f1a" : "#ffffff";
  const tint = hexToRgba(accent, prefersDark ? 0.12 : 0.06);
  const soft = hexToRgba(accent, prefersDark ? 0.16 : 0.08);
  const grad =
    `radial-gradient(1100px 540px at 15% -10%, ${hexToRgba(accent, prefersDark ? 0.22 : 0.16)} 0%, transparent 60%),
     radial-gradient(900px 480px at 110% 0%, ${hexToRgba(accent, prefersDark ? 0.18 : 0.12)} 0%, transparent 55%),
     linear-gradient(180deg, ${hexToRgba(accent, prefersDark ? 0.14 : 0.06)} 0%, ${hexToRgba(accent, prefersDark ? 0.06 : 0.02)} 100%)`;
  el.style.setProperty("--bg-base", base);
  el.style.setProperty("--bg-tint", tint);
  el.style.setProperty("--bg-soft", soft);
  el.style.setProperty("--bg-grad", grad);

  el.style.setProperty("--accent", accent);
  el.style.setProperty("--accent-04", hexToRgba(accent, 0.04));
  el.style.setProperty("--accent-10", hexToRgba(accent, 0.10));
  el.style.setProperty("--accent-12", hexToRgba(accent, 0.12));
  el.style.setProperty("--accent-18", hexToRgba(accent, 0.18));
  el.style.setProperty("--accent-30", hexToRgba(accent, 0.30));
  el.style.setProperty("--accent-35", hexToRgba(accent, 0.35));
  el.style.setProperty("--ring", hexToRgba(accent, 0.30));

  el.style.setProperty("--bs-primary", primary);
  el.style.setProperty("--bs-link-color", primary);
  el.style.setProperty("--bs-focus-ring-color", hexToRgba(primary, 0.25));
};

/* ───────────────── motion helpers ───────────────── */
const reduceMotion = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const motionMap = new WeakMap();
const lerp = (a, b, t) => a + (b - a) * t;
const startMotionLoop = (el) => {
  if (reduceMotion || motionMap.has(el)) return;
  const s = { ax: 0.5, ay: 0.5, tx: 0.5, ty: 0.5, rx: 0, ry: 0, raf: 0 };
  motionMap.set(el, s);
  const step = () => {
    s.ax = lerp(s.ax, s.tx, 0.18); s.ay = lerp(s.ay, s.ty, 0.18);
    const trX = (s.ay - 0.5) * 10, trY = (s.ax - 0.5) * -10;
    s.rx = lerp(s.rx, trX, 0.22); s.ry = lerp(s.ry, trY, 0.22);
    el.style.setProperty("--mx", `${s.ax * 100}%`);
    el.style.setProperty("--my", `${s.ay * 100}%`);
    el.style.setProperty("--rx", `${s.rx.toFixed(3)}deg`);
    el.style.setProperty("--ry", `${s.ry.toFixed(3)}deg`);
    el.style.setProperty("--lift", (8 + Math.hypot(s.rx, s.ry)).toFixed(2));
    if (Math.abs(s.tx - s.ax) > 0.001 || Math.abs(s.ty - s.ay) > 0.001) s.raf = requestAnimationFrame(step);
    else { cancelAnimationFrame(s.raf); motionMap.delete(el); }
  };
  s.raf = requestAnimationFrame(step);
};
const handleNavEnter = (e) => startMotionLoop(e.currentTarget);
const handleNavMotion = (e) => {
  const el = e.currentTarget, r = el.getBoundingClientRect();
  const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
  if (!motionMap.has(el)) startMotionLoop(el);
  const s = motionMap.get(el);
  s.tx = Math.min(1, Math.max(0, px)); s.ty = Math.min(1, Math.max(0, py));
  el.style.setProperty("--rpx", `${e.clientX - r.left}px`);
  el.style.setProperty("--rpy", `${e.clientY - r.top}px`);
};
const resetNavMotion = (e) => { const s = motionMap.get(e.currentTarget); if (s) { s.tx = 0.5; s.ty = 0.5; } e.currentTarget.classList.remove("rippling"); };
const rippleAtClick = (e) => {
  const el = e.currentTarget, r = el.getBoundingClientRect();
  el.style.setProperty("--rpx", `${e.clientX - r.left}px`);
  el.style.setProperty("--rpy", `${e.clientY - r.top}px`);
  el.classList.remove("rippling"); void el.offsetWidth; el.classList.add("rippling");
};

/* ---------------- component ---------------- */
export default function StudentLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { theme, toggleTheme } = useTheme();
  const brand = useBrand();
  const { profile } = useAuth();

  const [open, setOpen] = useState(false);
  const closeNav = useCallback(() => setOpen(false), []);
  const toggleNav = () => setOpen((s) => !s);

  useEffect(() => { closeNav(); }, [pathname, closeNav]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeNav(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeNav]);

  const schoolId = useMemo(
    () => profile?.schoolId || localStorage.getItem("schoolId") || null,
    [profile]
  );

  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");
  const [userEmail] = useState(() => auth.currentUser?.email || "");

  const [schoolName, setSchoolName] = useState("School");
  const [schoolLogo, setSchoolLogo] = useState("");

  // Live-load school name + logo
  useEffect(() => {
    if (!schoolId) return;
    let alive = true;
    const unsub = onSnapshot(
      doc(db, "schools", schoolId),
      async (snap) => {
        if (!snap.exists()) {
          if (alive) { setSchoolName("School"); setSchoolLogo(""); }
          return;
        }
        const data = snap.data() || {};
        if (alive) setSchoolName(data.name || "School");
        let url = await resolveLogoUrl(data);
        if (!url) url = await resolveLogoFromBrandingFolder(schoolId);
        if (alive) setSchoolLogo(url || "");
      },
      () => { if (alive) setSchoolLogo(""); }
    );
    return () => { alive = false; unsub && unsub(); };
  }, [schoolId]);

  // 3D-first nav (falls back to Bootstrap icons)
  const items = [
    { to: "/student",                 label: "Dashboard",     icon: "bi-grid",            img: STUDENT_3D.dashboard },
    { to: "/student/profile",         label: "Profile",       icon: "bi-person-badge",    img: STUDENT_3D.profile },
    { to: "/student/fees",            label: "Fees",          icon: "bi-cash-coin",       img: STUDENT_3D.fees },
    { to: "/student/classes",         label: "Online Classes",icon: "bi-camera-video",    img: STUDENT_3D.classes },
    { to: "/student/exams/examspage", label: "Exams/Results", icon: "bi-pencil-square",   img: STUDENT_3D.exams },
    { to: "/student/assignments",     label: "Assignments",   icon: "bi-journal-check",   img: STUDENT_3D.assignments },
    { to: "/student/progresscard",    label: "Progress Card", icon: "bi-graph-up-arrow",  img: STUDENT_3D.progress },
    { to: "/student/attendance",      label: "Attendance",    icon: "bi-clipboard-check", img: STUDENT_3D.attendance },
    { to: "/student/notices",         label: "Circulars",     icon: "bi-megaphone",       img: STUDENT_3D.circulars },
    { to: "/student/support",         label: "Support",       icon: "bi-ticket-perforated", img: STUDENT_3D.support },
    { to: "/student/settings",        label: "Settings",      icon: "bi-gear",            img: STUDENT_3D.settings },
  ];

  const go = (to) => navigate(to);

  const doLogout = async () => {
    const res = await Swal.fire({
      title: "Log out?",
      text: "You’ll need to sign in again to access your student portal.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, log me out",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      background: getComputedStyle(document.body).backgroundColor,
      color: getComputedStyle(document.body).color,
      confirmButtonColor: brand.accent || brand.primary,
      cancelButtonColor: hexToRgba(brand.accent || brand.primary, 0.35),
    });
    if (!res.isConfirmed) return;
    try { await signOut(auth); } catch {}
    localStorage.removeItem("schoolId");
    localStorage.removeItem("studentId");
    localStorage.removeItem("studentName");
    toast.success("Logged out — see you soon!", { autoClose: 1800 });
    navigate("/login?role=student", { replace: true });
  };

  const schoolInitial = (schoolName || "S").trim().charAt(0).toUpperCase();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // BRAND → CSS tokens
  const accent = brand.accent || brand.primary;
  useEffect(() => {
    const scope = document.getElementById("student-root");
    const targets = [scope, document.documentElement, document.body].filter(Boolean);
    targets.forEach((el) => paint(el, { primary: brand.primary, accent }));
    try {
      localStorage.setItem("brand.primary", normalizeHex6(brand.primary) || brand.primary);
      localStorage.setItem("brand.accent",  normalizeHex6(accent) || accent);
      localStorage.setItem("accentColor",   normalizeHex6(accent) || accent);
    } catch {}
  }, [brand.primary, accent]);

  const wrapperVars = {
    "--brand-primary": brand.primary,
    "--brand-accent": accent,
    "--on-brand": onColorFor(brand.primary),
    "--on-primary": onColorFor(brand.primary),
    "--on-accent": onColorFor(accent),
    "--accent": accent,
    "--accent-04": hexToRgba(accent, 0.04),
    "--accent-10": hexToRgba(accent, 0.10),
    "--accent-12": hexToRgba(accent, 0.12),
    "--accent-18": hexToRgba(accent, 0.18),
    "--accent-30": hexToRgba(accent, 0.30),
    "--accent-35": hexToRgba(accent, 0.35),
    "--ring": hexToRgba(accent, 0.30),
    "--bg-base": theme === "dark" ? "#0b0f1a" : "#ffffff",
    "--bg-tint": hexToRgba(accent, theme === "dark" ? 0.12 : 0.06),
    "--bg-soft": hexToRgba(accent, theme === "dark" ? 0.16 : 0.08),
    "--bg-grad":
      `radial-gradient(1100px 540px at 15% -10%, ${hexToRgba(accent, theme === "dark" ? 0.22 : 0.16)} 0%, transparent 60%),
       radial-gradient(900px 480px at 110% 0%, ${hexToRgba(accent, theme === "dark" ? 0.18 : 0.12)} 0%, transparent 55%),
       linear-gradient(180deg, ${hexToRgba(accent, theme === "dark" ? 0.14 : 0.06)} 0%, ${hexToRgba(accent, theme === "dark" ? 0.06 : 0.02)} 100%)`,
  };

  const onPickTheme = (hex) => {
    const v = normalizeHex6(hex) || hex;
    brand.setPrimary?.(v);
    brand.setAccent?.(v);
     // Fallback: paint immediately if setters aren’t provided
 if (!brand.setPrimary || !brand.setAccent) {
   const scope = document.getElementById("student-root");
   paint(scope, { primary: v, accent: v });
 }
  };

  return (
    <div className={`student-shell ${open ? "drawer-open" : ""}`} style={wrapperVars} id="student-root">
      <ParticlesBackground theme={theme} />

      {/* Sidebar / Drawer */}
      <aside className={`student-nav ${open ? "open" : ""}`} aria-hidden={!open}>
        <div className="nav-header">
          <div className="d-flex align-items-center gap-2">
            {schoolLogo ? (
              <img src={schoolLogo} alt={schoolName} className="rounded-3 border" style={{ width: 36, height: 36, objectFit: "cover" }} />
            ) : (
              <div
                className="rounded-3 d-inline-flex align-items-center justify-content-center"
                style={{ width: 36, height: 36, background: "var(--accent-10)", border: "1px solid var(--accent-35)", color: "var(--brand-accent)", fontWeight: 700 }}
                aria-label="School logo"
              >
                {schoolInitial}
              </div>
            )}
            <div className="lh-sm">
              <div className="fw-semibold text-truncate" title={schoolName}>{schoolName}</div>
              <small className="text-muted">Student</small>
            </div>
          </div>
        </div>

        <div className="nav-user">
          <div className="avatar" style={{ background: "var(--accent-10)", color: "var(--brand-accent)", borderColor: "var(--accent-30)" }}>
            {(studentName || "S").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="fw-semibold text-truncate">{studentName || "Student"}</div>
            <div className="small text-muted text-truncate">{userEmail}</div>
          </div>

          <button className="btn btn-sm btn-ghost rounded-circle" title={`Switch to ${theme === "light" ? "dark" : "light"} mode`} onClick={toggleTheme}>
            <i className={`bi ${theme === "light" ? "bi-moon" : "bi-sun"}`} />
          </button>
        </div>

        <div className="nav-items">
          {items.map((it) => {
            const active = it.to === "/student" ? pathname === "/student" : pathname.startsWith(it.to);
            return (
              <button
                key={it.to}
                className={`nav-item neo ${active ? "active" : ""}`}
                onClick={() => go(it.to)}
                onMouseEnter={handleNavEnter}
                onMouseMove={handleNavMotion}
                onMouseLeave={resetNavMotion}
                onMouseDown={rippleAtClick}
              >
                <span className="nav-aurora" aria-hidden />
                <span className="nav-caustic" aria-hidden />
                <span className="nav-spec" aria-hidden />
                <span className="nav-glare" aria-hidden />
                <span className="nav-ripple" aria-hidden />

                {/* 3D icon first, fallback to Bootstrap icon */}
                {it.img ? (
                  <img src={it.img} alt="" className="nav-ico-img" />
                ) : (
                  <span className="nav-ico"><i className={`bi ${it.icon}`} /></span>
                )}
                <span className="nav-label">{it.label}</span>
              </button>
            );
          })}
        </div>

        <div className="nav-footer d-grid gap-2">
          <ThemeColorPicker active={accent} onPick={onPickTheme} size="sm" align="start" fullWidth />
          <Button variant="outline-danger" className="w-100" onClick={doLogout}>
            <i className="bi bi-box-arrow-right me-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Backdrop for mobile drawer */}
      <div className={`drawer-backdrop ${open ? "show" : ""}`} onClick={closeNav} />

      {/* Main */}
      <main className="student-main">
        <div className={`topbar pill ring ${scrolled ? "scrolled" : ""}`}>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-sm btn-outline-secondary d-lg-none" onClick={toggleNav} aria-label="Toggle menu">
              <i className="bi bi-list" />
            </button>

            {/* 3D brand glyph in topbar */}
            {STUDENT_3D.dashboard ? (
              <img src={STUDENT_3D.profile} alt="" className="tb-ico-3d d-none d-lg-inline-block" />
            ) : (
              <i className="bi bi-layout-sidebar d-none d-lg-inline" />
            )}
            <span>Student</span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div className="d-none d-lg-inline-flex">
              <ThemeColorPicker active={accent} onPick={onPickTheme} />
            </div>

            <button className="btn btn-sm btn-ghost d-none d-lg-inline-flex" title={`Switch to ${theme === "light" ? "dark" : "light"} mode`} onClick={toggleTheme}>
              <i className={`bi ${theme === "light" ? "bi-moon" : "bi-sun"}`} />
            </button>
            <Button className="btn btn-sm btn-danger glow" title="Logout" onClick={doLogout}>
              <i className="bi bi-box-arrow-right" />
            </Button>
            <Badge bg="light" text="dark" className="border">{studentName || "Student"}</Badge>
          </div>
        </div>

        <div className="content-wrap">
          <Outlet />
        </div>
      </main>
      <ToastContainer
        position="bottom-right"
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        theme={theme === "dark" ? "dark" : "light"}
      />

      {/* Styles */}
    <style>{`
/* ====== Global mobile safety ====== */
* { box-sizing: border-box; }
html, body { max-width: 100%; overflow-x: hidden; }
.student-shell { overflow-x: hidden; }

.particles-bg,
.tsparticles-canvas-el,
[data-particles],
[data-particles-root]{
  position: fixed !important;
  inset: 0 !important;
  z-index: 0 !important;
  pointer-events: none !important;
}

/* ====== Shell ====== */
.student-shell{
  display:flex; min-height:100dvh; position:relative;
  background: var(--bg-grad), var(--bg-base);
  background-attachment: fixed;
}

/* ====== Drawer / Sidebar ====== */
.student-nav{
  width: 268px; flex: 0 0 268px;
  border-right: 1px solid var(--bs-border-color);
  padding: 12px; display:flex; flex-direction: column; gap: 12px;
  position: sticky; top: 0; height: 100dvh;
  background: color-mix(in oklab, var(--bg-base) 90%, var(--bg-tint));
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  z-index: 10;
}
@media (max-width: 992px){
  .student-nav{
    position: fixed; inset: 0 auto 0 0; height: 100dvh;
    transform: translateX(-100%);
    transition: transform .32s cubic-bezier(.18,.88,.24,1.08);
    overflow-y: auto; overscroll-behavior: contain;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    padding-top: max(12px, env(safe-area-inset-top));
    background: color-mix(in oklab, var(--bg-base) 92%, var(--bg-tint));
    z-index: 1000; /* above everything */
  }
  .student-nav.open{ transform: translateX(0); }
  .drawer-backdrop{
    position: fixed; inset: 0; background: rgba(0,0,0,.35);
    opacity: 0; visibility: hidden; transition: opacity .22s ease, backdrop-filter .22s ease;
    backdrop-filter: blur(0px);
    z-index: 999;
  }
  .drawer-backdrop.show{ opacity: 1; visibility: visible; backdrop-filter: blur(2px); }
  .student-shell.drawer-open{ overflow: hidden; }
}

/* Header & user card */
.nav-header{ padding: 6px 6px 2px 6px; }
.nav-user{
  display:flex; align-items:center; gap:10px;
  padding: 10px; border: 1px solid var(--bs-border-color);
  border-radius: 14px;
  background: color-mix(in oklab, var(--bg-base) 96%, var(--bg-tint));
  animation: cardFade .35s ease both;
}
.nav-user .avatar{
  width: 34px; height: 34px; border-radius: 10px;
  font-weight:700; display:flex; align-items:center; justify-content:center;
  border: 1px solid var(--accent-30);
}

/* ====== Topbar (single source of truth) ====== */
.student-main{ flex:1; display:flex; flex-direction:column; position:relative; z-index: 1; min-width: 0; }

.topbar{
  height: 52px; display:flex; align-items:center; justify-content:space-between;
  padding: 0 12px;
  position: sticky; top: max(8px, env(safe-area-inset-top));
  margin: 8px 12px 0;
  z-index: 5;
  border: 1px solid color-mix(in oklab, var(--brand-accent) 18%, var(--bs-border-color));
  border-radius: 999px;
  background: color-mix(in oklab, var(--bg-base) 98%, var(--bg-tint));
  -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
  box-shadow: 0 2px 10px rgba(0,0,0,.05);
  overflow: visible;  /* allow ring to render */
  isolation: isolate; /* ring behind content */
}

/* Gradient ring */
.topbar::before{
  content:""; position:absolute; inset:-2px; border-radius:999px; padding:2px;
  background:
    linear-gradient(135deg,
      color-mix(in oklab, var(--brand-accent) 55%, #fff) 0%,
      color-mix(in oklab, var(--brand-accent) 30%, #fff) 35%,
      color-mix(in oklab, var(--brand-accent) 18%, transparent) 100%);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude;
  opacity: .75; pointer-events:none; z-index:0;
}
.topbar::after{
  content:""; position:absolute; inset:0; border-radius:999px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.65);
  pointer-events:none; z-index:0;
}

.topbar.scrolled{
  background: color-mix(in oklab, var(--bg-base) 96%, var(--bg-soft));
  border-color: color-mix(in oklab, var(--brand-accent) 28%, var(--bs-border-color));
  box-shadow: 0 6px 18px rgba(0,0,0,.08);
}

.tb-ico-3d{
  width: 22px; height: 22px; object-fit: contain; border-radius: 8px;
  background: var(--accent-10); border: 1px solid var(--accent-35); margin-right: 2px;
}

@media (max-width: 576px){
  .topbar{ margin-left: max(12px, env(safe-area-inset-left));
           margin-right: max(12px, env(safe-area-inset-right)); }
}

/* ====== Content ====== */
.content-wrap{
  padding: 16px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  animation: fadeSlide .28s ease both;
}
@media (max-width: 992px){ .content-wrap{ padding: 12px; } }
@keyframes fadeSlide{ from{ opacity: 0; transform: translateY(6px); } to{ opacity: 1; transform: none; } }
@keyframes cardFade{ from{ opacity: 0; transform: translateY(4px); } to{ opacity: 1; transform: none; } }

/* ====== Nav tiles (3D) ====== */
.nav-items{ display:flex; flex-direction:column; gap:8px; padding-top:4px; perspective: 900px; }
.nav-item{
  position: relative; overflow: hidden;
  border: 1px solid var(--bs-border-color);
  background:
    linear-gradient(180deg,
      color-mix(in oklab, var(--bg-base) 96%, transparent) 0%,
      color-mix(in oklab, var(--bg-tint) 70%, transparent) 100%);
  border-radius: 14px; padding: 10px;
  display:flex; align-items:center; gap:10px; text-align:left;
  transition: background .15s ease, box-shadow .15s ease, border-color .15s ease, transform .1s ease, filter .15s ease;
}
.nav-item:hover{
  transform: translateY(-1px);
  background: color-mix(in oklab, var(--brand-accent) 8%, transparent);
  box-shadow: 0 10px 24px color-mix(in srgb, var(--brand-accent) 14%, transparent);
}
.nav-item.active{
  background:
    linear-gradient(180deg,
      color-mix(in oklab, var(--brand-accent) 18%, transparent) 0%,
      color-mix(in oklab, var(--brand-accent) 10%, transparent) 100%);
  border-color: var(--accent-35);
  box-shadow: 0 0 0 .18rem var(--accent-12), 0 12px 28px color-mix(in srgb, var(--brand-accent) 18%, transparent);
}
.nav-item.active::before{ content:""; position:absolute; left:0; top:0; bottom:0; width:4px; background: var(--brand-accent); }

/* 3D tilt & FX */
.nav-item.neo{ --rx: 0deg; --ry: 0deg; --mx: 50%; --my: 50%; --lift: 8;
  transform: perspective(900px) rotateX(var(--rx)) rotateY(var(--ry));
  transform-style: preserve-3d; will-change: transform, box-shadow, filter;
  box-shadow: 0 calc(var(--lift) * .8px) calc(var(--lift) * 3px) color-mix(in srgb, var(--brand-accent) 14%, transparent), 0 1px 0 rgba(255,255,255,.06) inset;
}
.nav-item.neo .nav-aurora{ position:absolute; inset:-1px; border-radius:16px; padding:1px;
  background: conic-gradient(from 210deg at 50% 50%,
    color-mix(in srgb, var(--brand-accent) 80%, white 0%) 0%,
    rgba(255,255,255,.85) 25%,
    color-mix(in srgb, var(--brand-primary) 80%, white 0%) 50%,
    rgba(255,255,255,.85) 75%,
    color-mix(in srgb, var(--brand-accent) 80%, white 0%) 100%);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude; opacity:.20; filter: blur(.35px); pointer-events:none;
}
.nav-item.neo:hover .nav-aurora{ opacity:.30; }
.nav-item.neo.active .nav-aurora{ opacity:.40; }
.nav-item.neo .nav-caustic{
  position:absolute; inset:-10%; pointer-events:none; mix-blend-mode:screen; opacity:.28;
  background: radial-gradient(180px 120px at var(--mx) var(--my),
      color-mix(in srgb, var(--brand-accent) 26%, white 0%) 0%,
      rgba(255,255,255,.15) 30%, rgba(255,255,255,0) 70%);
  filter: blur(8px); transition: opacity .2s ease;
}
.nav-item.neo:hover .nav-caustic{ opacity:.48; }
.nav-item.neo .nav-glare{
  position:absolute; inset:0; pointer-events:none; mix-blend-mode:screen;
  background: radial-gradient(220px 140px at var(--mx) var(--my),
    rgba(255,255,255,.18), rgba(255,255,255,.08) 40%, rgba(255,255,255,0) 64%);
  opacity:.52; transition: opacity .2s ease;
}
.nav-item.neo:hover .nav-glare{ opacity:.75; }

.nav-ico-img{
  width: 34px; height: 34px; object-fit: contain; border-radius: 10px; padding: 4px;
  background: color-mix(in oklab, var(--brand-accent) 9%, transparent);
  border: 1px solid color-mix(in oklab, var(--brand-accent) 30%, var(--bs-border-color));
  box-shadow: 0 10px 22px color-mix(in oklab, var(--brand-accent) 15%, transparent);
  transform: translateZ(calc(var(--lift) * 1.1px));
  transition: transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s ease, filter .22s ease;
}
.nav-item.neo:hover .nav-ico-img{
  transform: translateZ(calc(var(--lift) * 1.2px)) scale(1.06) rotate(-1.2deg);
  box-shadow: 0 8px 18px color-mix(in srgb, var(--brand-accent) 22%, transparent);
  filter: saturate(1.05);
}

.nav-label{ transition: transform .18s ease, letter-spacing .18s ease; }
.nav-item:hover .nav-label{ transform: translateX(1px); letter-spacing: .1px; }

/* Dropdown menu: always above sticky items/effects */
.theme-picker.dropdown-menu{
  position: fixed !important;
  z-index: 5000 !important;
  min-width: 260px;
  background: var(--bs-body-bg, #fff);
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0,0,0,.16), 0 2px 10px rgba(0,0,0,.08);
}

/* Buttons */
.btn-ghost{
  border: 1px solid var(--accent-35);
  background: var(--accent-10);
  color: var(--brand-accent);
  border-radius: 10px;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease, border-color .15s ease;
}
.btn-ghost:hover{
  background: var(--accent-18); border-color: var(--accent-35);
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 .45rem 1.1rem color-mix(in oklab, var(--brand-accent) 18%, transparent);
}
.btn.glow{ box-shadow: 0 0 0 0 rgba(220,53,69,.25); transition: box-shadow .15s ease, transform .15s ease; }
.btn.glow:hover{ transform: translateY(-1px); box-shadow: 0 .5rem 1.2rem rgba(220,53,69,.25); }

/* Mobile perf: lighten FX */
@media (max-width: 576px){
  .nav-item.neo .nav-caustic{ filter: blur(6px); opacity:.32; }
  .nav-item.neo .nav-glare{ opacity:.58; }
}

/* Reduced-motion */
@media (prefers-reduced-motion: reduce){
  .nav-items{ perspective:none; }
  .nav-item.neo{ transform:none !important; }
  .nav-item.neo::after,
  .nav-item.neo .nav-aurora,
  .nav-item.neo .nav-glare,
  .nav-item.neo .nav-spec,
  .nav-item.neo .nav-caustic{ animation:none !important; transition:none !important; }
}
`}</style>

    </div>
  );
}

/* ───────────────── ThemeColorPicker (mobile-safe) ───────────────── */
function ThemeColorPicker({ active, onPick, size = "sm", align = "end", fullWidth = false }) {
  const normalizeHex6 = (hex) => {
    let v = String(hex || "").trim().replace("#", "");
    if (v.length === 3) v = v.split("").map((x) => x + x).join("");
    if (/^[0-9a-f]{6}$/i.test(v)) return `#${v.toLowerCase()}`;
    return null;
  };
  const ACCENTS = [
    "#0d6efd","#3b82f6","#1d4ed8","#60a5fa","#0ea5e9","#38bdf8","#0284c7",
    "#4f46e5","#4338ca","#dc3545","#ef4444","#991b1b","#f87171","#f43f5e","#e11d48","#b91c1c",
    "#fb7185","#fca5a5","#be123c","#198754","#22c55e","#065f46","#86efac","#10b981","#059669",
    "#16a34a","#15803d","#84cc16","#a3e635","#ffc107","#facc15","#d97706","#fde047","#f59e0b",
    "#eab308","#ca8a04","#ffd166","#6f42c1","#8b5cf6","#7e22ce","#c4b5fd","#a855f7","#9333ea",
    "#7c3aed","#d8b4fe","#20c997","#06b6d4","#164e63","#67e8f9","#22d3ee","#14b8a6","#0d9488",
    "#2dd4bf","#fd7e14","#f97316","#9a3412","#fb923c","#ea580c","#c2410c","#ec4899","#db2777",
    "#be185d","#f472b6","#d946ef","#fda4af","#6c757d","#94a3b8","#334155","#e2e8f0","#9ca3af",
    "#6b7280","#374151","#1f2937","#111827","#f3f4f6","#f8fafc","#fafafa","#000000","#ffffff",
  ];
  const activeHex = normalizeHex6(active) || active;
  return (
    <Dropdown align={align} autoClose="outside" flip renderMenuOnMount container="body" popperConfig={{ strategy: "fixed", modifiers: [{ name: "offset", options: { offset: [0, 8] } }] }}className={fullWidth ? "w-100" : ""}>
      <Dropdown.Toggle size={size} variant="outline-secondary" className={`theme-toggle d-inline-flex align-items-center gap-2 ${fullWidth ? "w-100 justify-content-start" : ""}`}>
        <span className="theme-chip" style={{ background: activeHex }} />
        Theme Color
      </Dropdown.Toggle>
      <Dropdown.Menu className="theme-picker shadow">
        <div className="swatch-grid">
          {ACCENTS.map((col) => {
            const hex = normalizeHex6(col) || col;
            const isLight = ["#ffffff", "#e2e8f0", "#f3f4f6", "#fafafa", "#f8fafc"].includes(hex);
            const isActive = (normalizeHex6(activeHex) || activeHex) === hex;
            return (
              <button
                key={hex}
                type="button"
                className={`swatch-btn ${isActive ? "active" : ""}`}
                style={{ background: hex, border: isLight ? "1px solid rgba(0,0,0,.25)" : "none" }}
                onClick={() => onPick?.(hex)}
                aria-label={`Pick ${hex}`}
                title={hex}
              >
                {isActive ? <span className="swatch-ring" /> : null}
              </button>
            );
          })}
          <label className="swatch-btn custom" title="Custom color">
            <span className="plus">＋</span>
            <input type="color" className="visually-hidden" onChange={(e) => onPick?.(e.target.value)} />
          </label>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}
